import type { Section, SectionNode } from './types';

/** The forest of sections (roots first, siblings in the list's order). Unknown parents become roots. */
export function buildTree(items: readonly Section[]): SectionNode[] {
  const known = new Set(items.map((s) => s.id));
  const nodes = new Map<string, SectionNode>(
    items.map((s) => [s.id, { ...s, children: [], depth: 1 }]),
  );
  const roots: SectionNode[] = [];
  for (const item of items) {
    const node = nodes.get(item.id);
    if (!node) continue;
    const parent =
      item.parent_id !== null && known.has(item.parent_id) ? nodes.get(item.parent_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const level = (list: SectionNode[], depth: number) => {
    for (const node of list) {
      node.depth = depth;
      level(node.children, depth + 1);
    }
  };
  level(roots, 1);
  return roots;
}

/** The ancestors of a section from the top down, ending with the section itself. */
export function pathOf(items: readonly Section[], id: string | null | undefined): Section[] {
  const by = new Map(items.map((s) => [s.id, s]));
  const path: Section[] = [];
  const seen = new Set<string>();
  let current = id ? by.get(id) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parent_id ? by.get(current.parent_id) : undefined;
  }
  return path;
}

/** "Development / Backend": where a resource is filed (`''` for the workspace root). */
export const pathLabel = (items: readonly Section[], id: string | null | undefined): string =>
  pathOf(items, id)
    .map((s) => s.name)
    .join(' / ');

/** Every section below `id` (not itself). */
export function descendantIds(items: readonly Section[], id: string): Set<string> {
  const found = new Set<string>();
  const stack = [id];
  while (stack.length > 0) {
    const current = stack.pop();
    for (const item of items) {
      if (item.parent_id === current && !found.has(item.id)) {
        found.add(item.id);
        stack.push(item.id);
      }
    }
  }
  return found;
}

/** Levels of the subtree below and including `id`. */
export function heightOf(items: readonly Section[], id: string): number {
  return (
    1 + Math.max(0, ...items.filter((s) => s.parent_id === id).map((s) => heightOf(items, s.id)))
  );
}

/**
 * Where a SECTION may be moved (the server checks again): not into itself or a descendant, and
 * not so deep that its subtree passes the depth limit. `null` (the root) is always allowed.
 */
export function sectionDestinations(
  items: readonly Section[],
  moving: string,
  maxDepth: number,
): Set<string | null> {
  const blocked = descendantIds(items, moving);
  blocked.add(moving);
  const height = heightOf(items, moving);
  const allowed = new Set<string | null>([null]);
  for (const item of items) {
    if (!blocked.has(item.id) && pathOf(items, item.id).length + height <= maxDepth) {
      allowed.add(item.id);
    }
  }
  return allowed;
}
